import { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Images } from "lucide-react";
import foodBuffet from "@/assets/food-buffet-enhanced.jpg";
import eventWedding1 from "@/assets/pavilion-outdoor.jpeg";
import eventWedding2 from "@/assets/event-wedding2.jpg";
import winterWonderland from "@/assets/winter-wonderland.png";
import roastedVegetables from "@/assets/roasted-vegetables-vibrant.jpg";
import croissantSandwiches from "@/assets/croissant-sandwiches-vibrant.jpg";
import dessertBreadPudding from "@/assets/dessert-bread-pudding-vibrant.jpg";
import mocktails from "@/assets/mocktails.webp";
import eventStations from "@/assets/event-stations.webp";
import eventCorporate from "@/assets/event-corporate-gala.jpg";
import eventQuinceanera from "@/assets/event-quinceanera.jpg";
import eventBirthday from "@/assets/event-birthday.jpg";
import kidsPavilionParty from "@/assets/kids-pavilion-party.png";
import eventPavilion from "@/assets/event-pavilion-reception.jpg";
import pavilionBirthday from "@/assets/pavilion-birthday-party.jpg";
import winterWonderlandNew from "@/assets/winter-wonderland-new.png";
import eventChristmasParty from "@/assets/event-christmas-party.png";

type GalleryCategory = "featured" | "food" | "events";

const FoodGallery = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<GalleryCategory>("featured");

  const allImages = {
    featured: [
      { src: eventStations, alt: "Elegant catering buffet station" },
      { src: foodBuffet, alt: "Chef Paul's catering buffet display" },
      { src: roastedVegetables, alt: "Roasted vegetable platter with feta" },
      { src: croissantSandwiches, alt: "Chicken salad croissant sandwiches" },
      { src: dessertBreadPudding, alt: "Bread pudding with berries and cream" },
      { src: mocktails, alt: "Refreshing mocktail selection" },
    ],
    food: [
      { src: eventStations, alt: "Elegant catering buffet station" },
      { src: roastedVegetables, alt: "Roasted vegetable platter with feta" },
      { src: croissantSandwiches, alt: "Chicken salad croissant sandwiches" },
      { src: dessertBreadPudding, alt: "Bread pudding with berries and cream" },
      { src: mocktails, alt: "Refreshing mocktail selection" },
      { src: foodBuffet, alt: "Chef Paul's catering buffet display" },
      { src: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=1200&q=90", alt: "Fresh salmon with herbs" },
      { src: "https://images.unsplash.com/photo-1544025162-d76694265947?w=1200&q=90", alt: "Prime rib roast" },
      { src: "https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=1200&q=90", alt: "Gourmet appetizer platter" },
    ],
    events: [
      { src: eventChristmasParty, alt: "Christmas party in the ballroom" },
      { src: pavilionBirthday, alt: "Birthday party at the pavilion with string lights" },
      { src: kidsPavilionParty, alt: "Kids playing at the pavilion party" },
      { src: eventPavilion, alt: "Evening reception under the pavilion" },
      { src: winterWonderlandNew, alt: "Winter Wonderland themed holiday event" },
      { src: eventWedding2, alt: "Beautiful event centerpiece design" },
    ],
  };

  const galleryImages = allImages[activeCategory];

  const categories: { id: GalleryCategory; label: string; count: number }[] = [
    { id: "featured", label: "Featured", count: allImages.featured.length },
    { id: "food", label: "Cuisine", count: allImages.food.length },
    { id: "events", label: "Events", count: allImages.events.length },
  ];

  const openLightbox = (index: number) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);

  const goToPrevious = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === 0 ? galleryImages.length - 1 : selectedIndex - 1);
    }
  }, [selectedIndex, galleryImages.length]);

  const goToNext = useCallback(() => {
    if (selectedIndex !== null) {
      setSelectedIndex(selectedIndex === galleryImages.length - 1 ? 0 : selectedIndex + 1);
    }
  }, [selectedIndex, galleryImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, goToPrevious, goToNext]);

  return (
    <section id="gallery" className="section-padding bg-background">
      <div className="container-elegant">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-accent font-medium tracking-widest uppercase text-sm mb-3">
            Our Culinary Artistry
          </p>
          <h2 className="heading-section text-foreground mb-4">
            From Our Kitchen to Your Table
          </h2>
          <div className="flex justify-center mb-6">
            <span className="text-accent text-3xl">⚜</span>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
            Every dish is crafted with passion and precision. Browse our gallery to see 
            the quality and creativity Chef Paul brings to every event.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setSelectedIndex(null);
              }}
              className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                activeCategory === category.id
                  ? "bg-primary text-primary-foreground shadow-soft"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              <Images className="w-4 h-4" />
              {category.label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeCategory === category.id
                  ? "bg-primary-foreground/20"
                  : "bg-background"
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className={`rounded-xl overflow-hidden shadow-elevated group cursor-pointer animate-scale-in ${
                index === 0 && activeCategory === "featured" ? "md:col-span-2 md:row-span-2" : ""
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <img
                src={image.src}
                alt={image.alt}
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${
                  index === 0 && activeCategory === "featured" ? "h-full min-h-[280px] md:min-h-[400px]" : "h-48 md:h-56"
                } ${image.alt.toLowerCase().includes("salmon") ? "object-[center_40%]" : "object-center"}`}
              />
            </div>
          ))}
        </div>

        {/* View All hint */}
        {activeCategory === "featured" && (
          <div className="text-center mt-8">
            <button
              onClick={() => setActiveCategory("food")}
              className="text-accent hover:text-primary font-medium transition-colors inline-flex items-center gap-2"
            >
              View all {allImages.food.length} cuisine photos
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <Dialog open={selectedIndex !== null} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div className="relative flex items-center justify-center w-full h-full min-h-[60vh]">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Close lightbox"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-50 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {/* Image */}
            {selectedIndex !== null && (
              <div className="flex flex-col items-center justify-center p-8">
                <img
                  src={galleryImages[selectedIndex].src}
                  alt={galleryImages[selectedIndex].alt}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
                <p className="text-white/80 mt-4 text-center text-sm md:text-base">
                  {galleryImages[selectedIndex].alt}
                </p>
                <p className="text-white/50 text-sm mt-2">
                  {selectedIndex + 1} / {galleryImages.length}
                </p>
              </div>
            )}

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 z-50 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FoodGallery;
